import { Bus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { VehicleTypeBreakdown as VehicleTypeBreakdownType, BusPricingOption } from "@/types/eventPartner";

interface VehicleTypeBreakdownProps {
  breakdown: VehicleTypeBreakdownType[];
  busPricingInfo?: Record<string, BusPricingOption> | null;
  isLoading?: boolean;
}

export const VehicleTypeBreakdown = ({ breakdown, busPricingInfo, isLoading }: VehicleTypeBreakdownProps) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Merge breakdown with all available vehicle options from bus_pricing_info
  const mergedBreakdown = (() => {
    const breakdownMap = new Map(breakdown.map((b) => [b.vehicleType, b]));
    
    // If we have bus pricing info, add any missing vehicle types with 0 bookings
    if (busPricingInfo) {
      Object.values(busPricingInfo).forEach((option) => {
        if (!breakdownMap.has(option.user_friendly_name)) {
          breakdownMap.set(option.user_friendly_name, {
            vehicleType: option.user_friendly_name,
            count: 0,
            revenue: 0,
          });
        }
      });
    }
    
    // Sort by revenue (highest first), then by name for those with 0
    return Array.from(breakdownMap.values()).sort((a, b) => {
      if (b.revenue !== a.revenue) return b.revenue - a.revenue;
      return a.vehicleType.localeCompare(b.vehicleType);
    });
  })();

  const maxRevenue = Math.max(...mergedBreakdown.map((v) => v.revenue), 1);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bus className="h-5 w-5" />
            Bookings by Vehicle Type
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-4 bg-muted rounded w-32 mb-2" />
                <div className="h-2 bg-muted rounded w-full" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (mergedBreakdown.length === 0) {
    return (
      <Card className="bg-white border border-gray-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-gray-900">
            <Bus className="h-5 w-5" />
            Bookings by Vehicle Type
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-500 text-sm">No vehicle options available.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border border-gray-200 shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-gray-900">
          <Bus className="h-5 w-5" />
          Bookings by Vehicle Type
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {mergedBreakdown.map((vehicle) => {
            const percentage = maxRevenue > 0 ? (vehicle.revenue / maxRevenue) * 100 : 0;
            
            return (
              <div key={vehicle.vehicleType}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-900">
                    {vehicle.vehicleType}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className={`text-sm ${vehicle.count === 0 ? 'text-gray-400' : 'text-gray-500'}`}>
                      {vehicle.count} {vehicle.count === 1 ? "booking" : "bookings"}
                    </span>
                    <span className={`text-sm font-semibold ${vehicle.revenue === 0 ? 'text-gray-400' : 'text-green-600'}`}>
                      {formatCurrency(vehicle.revenue)}
                    </span>
                  </div>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${vehicle.revenue === 0 ? 'bg-gray-300' : 'bg-green-500'}`}
                    style={{ width: `${Math.max(percentage, vehicle.count === 0 ? 0 : 2)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
